/* Product configurator behaviour.
 *
 * Ported from src/pages/BuyWiredIndoorCamera.jsx, which holds all of this in
 * React state. Here the state lives in the DOM and the pieces that sit in
 * different Shopify sections — the rail, the sub-nav total and the sticky bar —
 * stay in sync through one document-level event.
 *
 * Everything is server-rendered in its default state, so with JS off the page
 * still shows the first gallery frame, the selected model and finish, the real
 * price and a working Add to Bag.
 *
 * Selection is single-axis: a model or a finish block may name a variant, and
 * choosing it selects that variant outright. The design's own configurator has
 * one model and one finish, so there is no model x finish matrix to resolve; a
 * product that needs one wants Shopify's option-based picker instead.
 */

const q = (sel, root = document) => root.querySelector(sel);
const qa = (sel, root = document) => [...root.querySelectorAll(sel)];

/* ── Price and variant sync ─────────────────────────────────── */

const VARIANT_EVENT = 'hf:buy-variant';

function applyVariant(detail) {
  if (detail.id) {
    qa('[data-hf-buy-id]').forEach((input) => {
      input.value = detail.id;
    });
  }

  if (detail.price) {
    qa('[data-hf-buy-price]').forEach((el) => {
      el.textContent = detail.price;
    });
  }

  if (detail.monthly) {
    qa('[data-hf-buy-monthly]').forEach((el) => {
      el.textContent = detail.monthly;
    });
  }

  qa('[data-hf-buy-add]').forEach((btn) => {
    btn.disabled = detail.available === false;
  });
}

document.addEventListener(VARIANT_EVENT, (event) => applyVariant(event.detail));

function selectVariant(button) {
  const { hfVariant, hfPrice, hfMonthly, hfAvailable } = button.dataset;
  if (!hfVariant) return;

  document.dispatchEvent(
    new CustomEvent(VARIANT_EVENT, {
      detail: {
        id: hfVariant,
        price: hfPrice,
        monthly: hfMonthly,
        available: hfAvailable !== 'false',
      },
    })
  );
}

/* ── Configurator ───────────────────────────────────────────── */

function initBuy(root) {
  const frames = qa('[data-hf-buy-frame]', root);
  const dots = qa('[data-hf-buy-dot]', root);
  const thumbs = qa('[data-hf-buy-thumb]', root);
  let index = 0;

  const showFrame = (next) => {
    if (!frames.length) return;
    index = (next + frames.length) % frames.length;
    frames.forEach((frame, i) => {
      const on = i === index;
      frame.classList.toggle('is-active', on);
      if (on) frame.removeAttribute('aria-hidden');
      else frame.setAttribute('aria-hidden', 'true');
    });

    dots.forEach((dot, i) => {
      const on = i === index;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    // Thumbnails cover only the frames the design labels, so a thumbnail maps to
    // a frame by index rather than by position in the strip.
    thumbs.forEach((thumb) => {
      thumb.classList.toggle('is-active', Number(thumb.dataset.hfBuyThumb) === index);
    });
  };

  dots.forEach((dot, i) => dot.addEventListener('click', () => showFrame(i)));
  thumbs.forEach((thumb) =>
    thumb.addEventListener('click', () => showFrame(Number(thumb.dataset.hfBuyThumb)))
  );
  q('[data-hf-buy-prev]', root)?.addEventListener('click', () => showFrame(index - 1));
  q('[data-hf-buy-next]', root)?.addEventListener('click', () => showFrame(index + 1));

  const scroller = q('[data-hf-buy-scroll]', root);
  q('[data-hf-buy-scroll-prev]', root)?.addEventListener('click', () =>
    scroller?.scrollBy({ left: -320, behavior: 'smooth' })
  );
  q('[data-hf-buy-scroll-next]', root)?.addEventListener('click', () =>
    scroller?.scrollBy({ left: 320, behavior: 'smooth' })
  );

  const group = (selector, onPick) => {
    const items = qa(selector, root);
    items.forEach((item) =>
      item.addEventListener('click', () => {
        items.forEach((other) => {
          const on = other === item;
          other.classList.toggle('is-active', on);
          other.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        onPick?.(item);
        selectVariant(item);
      })
    );
  };

  group('[data-hf-buy-model]');
  group('[data-hf-buy-colour]', (button) => {
    const label = q('[data-hf-buy-colour-name]', root);
    if (label && button.dataset.hfColour) label.textContent = button.dataset.hfColour;
  });
}

/* ── Product compare ────────────────────────────────────────── */

function initCompare(root) {
  const toggle = q('[data-hf-compare-toggle]', root);
  const panel = q('[data-hf-compare-panel]', root);

  toggle?.addEventListener('click', () => {
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    if (panel) panel.hidden = open;
  });

  // Each column holds a cell per comparable product with all but the active one
  // hidden, so switching columns is a visibility toggle and needs no data to be
  // shipped separately from the markup.
  const setColumn = (index, key) => {
    qa(`[data-hf-compare-slot="${index}"]`, root).forEach((slot) => {
      qa('[data-hf-compare-cam]', slot).forEach((cell) => {
        cell.hidden = cell.dataset.hfCompareCam !== key;
      });
    });
  };

  qa('[data-hf-compare-select]', root).forEach((select) => {
    const index = select.dataset.hfCompareSelect;
    const button = q('[data-hf-compare-select-btn]', select);
    const menu = q('[data-hf-compare-menu]', select);
    const column = select.closest('[data-hf-compare-col]');

    button?.addEventListener('click', (event) => {
      event.stopPropagation();
      const open = button.getAttribute('aria-expanded') === 'true';
      closeMenus();
      if (!open) {
        button.setAttribute('aria-expanded', 'true');
        if (menu) menu.hidden = false;
        column?.classList.add('is-open');
      }
    });

    qa('[data-hf-compare-option]', select).forEach((option) => {
      option.addEventListener('click', () => {
        const key = option.dataset.hfCompareOption;
        setColumn(index, key);
        qa('[data-hf-compare-option]', select).forEach((other) =>
          other.setAttribute('aria-current', other === option ? 'true' : 'false')
        );
        closeMenus();
      });
    });
  });

  function closeMenus() {
    qa('[data-hf-compare-select]', root).forEach((select) => {
      q('[data-hf-compare-select-btn]', select)?.setAttribute('aria-expanded', 'false');
      const menu = q('[data-hf-compare-menu]', select);
      if (menu) menu.hidden = true;
      select.closest('[data-hf-compare-col]')?.classList.remove('is-open');
    });
  }

  document.addEventListener('click', closeMenus);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeMenus();
  });
}

/* ── Boot ───────────────────────────────────────────────────── */

function boot() {
  qa('[data-hf-pdp-buy]').forEach((el) => {
    if (el.dataset.hfReady === '1') return;
    el.dataset.hfReady = '1';
    initBuy(el);
  });

  qa('[data-hf-pdp-compare]').forEach((el) => {
    if (el.dataset.hfReady === '1') return;
    el.dataset.hfReady = '1';
    initCompare(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// The theme editor re-renders one section at a time.
document.addEventListener('shopify:section:load', boot);
